import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { useGlobalStore } from '@/stores/globalStore';
import router from './router';

// Create Vue app
const app = createApp( App );

// Use Pinia for state management
const pinia = createPinia();
app.use( pinia );

// Initialize global settings (language detection) before mounting so
// components can read the selected language during setup. initLanguage()
// may perform a runtime fetch of a deployment YAML, so we await it here.
( async () => {
    try {
        const globalStore = useGlobalStore();
        await globalStore.initLanguage();

        // Update the favicon using shared utility; watch for runtime changes
        try {
            // Import the small utility that centralizes favicon behavior
            const { setFavicon } = await import( './utils/globalUtils' );

            // Initial set (use the store getter value)
            try {
                setFavicon( ( globalStore as any ).getDomainLogoImage );
            } catch ( _e ) {
                // ignore
            }

            // Watch for runtime changes and update favicon when the getter changes
            try {
                watch(
                    () => ( globalStore as any ).getDomainLogoImage,
                    ( val ) => setFavicon( val ),
                    { immediate: false }
                );
            } catch ( _e ) {
                // ignore if watch fails outside of Vue runtime
            }
            // Also watch the computed document title on the global store and update
            // the document title when it changes.
            try {
                const setDocumentTitle = ( t?: string | null ) => {
                    try {
                        if ( typeof document === 'undefined' ) return;
                        document.title = t || '';
                    } catch ( _e ) {
                        /* ignore */
                    }
                };

                // Initial set
                try {
                    setDocumentTitle( ( globalStore as any ).documentTitle );
                } catch ( _e ) {
                    /* ignore */
                }

                // Watch for changes
                try {
                    watch(
                        () => ( globalStore as any ).documentTitle,
                        ( v ) => setDocumentTitle( v ),
                        { immediate: false }
                    );
                } catch ( _e ) {
                    /* ignore */
                }
            } catch ( _e ) {
                // ignore dynamic import failures in non-bundled environments
            }
        } catch ( _e ) {
            // ignore dynamic import failures in non-bundled environments
        }
    } catch ( e ) {
        // non-fatal: continue even if store init fails
        // eslint-disable-next-line no-console
        console.error( 'Error initializing global store language:', e );
    }

    // Use Vue Router
    app.use( router );

    // Register navigation guard AFTER router is installed
    // This guard prevents accidental navigation away from an active assessment
    router.beforeEach( async ( to, from, next ) => {
        // Guard against leaving the assessment page without confirmation
        // (except when going to settings, which should be allowed mid-assessment)
        if ( from.name === 'assessment' && to.name !== 'settings' ) {
            try {
                // Dynamically import stores (they're already initialized at this point)
                const { useAssessmentStore } = await import( '@/stores/assessmentStore' );
                const { useUiStore } = await import( '@/stores/uiStore' );

                const assessmentStore = useAssessmentStore();
                const uiStore = useUiStore();

                // If UI requested to suppress the quit confirmation (e.g. during
                // the submit -> results flow), skip the guard entirely.
                if ( uiStore.suppressQuitConfirmation ) {
                    next();
                    return;
                }

                // Check if there's an active assessment
                if ( assessmentStore.currentAssessment ) {
                    const unanswered = assessmentStore.unansweredCount;
                    const total = assessmentStore.totalQuestions;

                    let message = 'Are you sure you want to quit this assessment?';
                    if ( unanswered < total ) {
                        message = `You have answered ${total - unanswered} of ${total} questions. Your progress will be lost. Are you sure you want to quit?`;
                    }

                    const confirmed = await uiStore.showConfirm( 'Quit Assessment', message, true );

                    if ( !confirmed ) {
                        // User cancelled - stay on assessment page
                        next( false );
                        return;
                    }

                    // User confirmed quitting the assessment — clear saved progress
                    // and reset in-memory assessment state so progress cannot be resumed.
                    try {
                        // clearSavedProgressForCurrent removes any persisted progress (localStorage)
                        if ( typeof assessmentStore.clearSavedProgressForCurrent === 'function' ) {
                            assessmentStore.clearSavedProgressForCurrent();
                        }
                    } catch ( e ) {
                        // non-fatal: log and continue with navigation
                        // eslint-disable-next-line no-console
                        console.error( '[GUARD] Error clearing saved progress on quit:', e );
                    }

                    try {
                        if ( typeof assessmentStore.resetAssessment === 'function' ) {
                            assessmentStore.resetAssessment();
                        }
                    } catch ( e ) {
                        // non-fatal: log and continue with navigation
                        // eslint-disable-next-line no-console
                        console.error( '[GUARD] Error resetting assessment state on quit:', e );
                    }
                }
            } catch ( e ) {
                // If store loading fails, allow navigation to proceed
                // eslint-disable-next-line no-console
                console.error( '[GUARD] Error checking assessment state:', e );
            }
        }

        next();
    } );

    // Mount the app
    app.mount( '#app' );
} )();
